import {Inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, ReplaySubject, Subject} from 'rxjs';
import {first, map, take} from 'rxjs/operators';
import {CountedAndPaginated} from '../interface/response/counted-and-paginated';
import {ListResponse} from '../interface/response/list-response';
import {Contract} from '../interface/contract';
import {FilterService} from './filter.service';
import {DOCUMENT} from '@angular/common';
import {Document as DocumentInterface} from '../interface/document';
import {ContractInsuranceCompany} from '../interface/contract-insurance-company';
import {Uuid} from '../interface/response/uuid';
import {ContractOfwcaReassign} from '../interface/contract-ofwca-reassign';
import {Ofwca} from '../interface/ofwca';
import {ContractOfwcaApplication} from '../interface/contract-ofwca-application';
import {ReportCustom} from "../interface/report-custom";
import {VatTaxpayers} from "../interface/vat-taxpayers";

@Injectable({
  providedIn: 'root'
})
export class ContractService {
  private readonly contractListSubject: ReplaySubject<CountedAndPaginated<Contract>> = new ReplaySubject<CountedAndPaginated<Contract>>(1);
  private readonly contractNotCountedListSubject: ReplaySubject<ListResponse<Contract>> = new ReplaySubject<ListResponse<Contract>>(1);
  public readonly contractInsuranceCompanyList: ReplaySubject<CountedAndPaginated<ContractInsuranceCompany>>
    = new ReplaySubject<CountedAndPaginated<ContractInsuranceCompany>>(1);

  constructor(
    private httpClient: HttpClient,
    private filterService: FilterService,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  public sendForm(formData: object, contractId?: string): Observable<any> {
    if (contractId) {
      return this.httpClient.put(`/rest/crm/contract/${contractId}`, formData)
        .pipe(
          first()
        );
    } else {
      return this.httpClient.post('/rest/crm/contract', formData)
        .pipe(
          first()
        );
    }
  }

  public loadList(page: number, pageSize: number, filters: object): void {
    this.httpClient.get(`/rest/crm/contract?page=${page + 1}&pageSize=${pageSize}&` + this.filterService.parseFilters(filters))
      .pipe(
        first()
      )
      .subscribe((result: CountedAndPaginated<Contract>) => {
        this.contractListSubject.next(result);
      });
  }

  public loadInsuranceCompanyList(page: number, pageSize: number, filters: object): void {
    this.httpClient.get(`/rest/crm/contract-insurance-company?page=${page + 1}&pageSize=${pageSize}&`
      + this.filterService.parseFilters(filters))
      .pipe(
        first()
      )
      .subscribe((result: CountedAndPaginated<ContractInsuranceCompany>) => {
        this.contractInsuranceCompanyList.next(result);
      });
  }

  public loadNotCountedList(page: number, pageSize: number, filters: object, subject?: Subject<ListResponse<Contract>>): void {
    this.httpClient
      .get(`/rest/crm/contract?disableCount=1&page=${page + 1}&pageSize=${pageSize}&` + this.filterService.parseFilters(filters))
      .pipe(take(1))
      .subscribe((result: ListResponse<Contract>) => {
        if (subject) {
          subject.next(result);
        } else {
          this.contractNotCountedListSubject.next(result);
        }
      });
  }

  public getContractListSubject(): ReplaySubject<CountedAndPaginated<Contract>> {
    return this.contractListSubject;
  }

  public getContractNotCountedListSubject(): ReplaySubject<ListResponse<Contract>> {
    return this.contractNotCountedListSubject;
  }

  public downloadReport(filters: object): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/contract/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  public get(contractId: string): Observable<Contract> {
    return this.httpClient.get(`/rest/crm/contract/${contractId}`)
      .pipe(first()) as Observable<Contract>;
  }

  public getContractDocuments(contractId: string): Observable<ListResponse<DocumentInterface>> {
    return this.httpClient.get(`/rest/crm/contract/${contractId}/document`)
      .pipe(first()) as Observable<ListResponse<DocumentInterface>>;
  }

  public deleteContract(uuid: string): Observable<any> {
    return this.httpClient.delete(`/rest/crm/contract/${uuid}`)
      .pipe(first());
  }

  public terminateContract(uuid: string, formData: object): Observable<any> {
    return this.httpClient.put(`/rest/crm/contract/${uuid}/terminate`, formData)
      .pipe(first());
  }

  public addInsuranceCompanies(contractId: string, formData: object): Observable<any> {
    return this.httpClient.post(`/rest/crm/contract/${contractId}/insurance-companies`, formData)
      .pipe(first());
  }

  public editContractInsuranceCompany(uuid: string, formData: object): Observable<any> {
    return this.httpClient.put(`/rest/crm/contract-insurance-company/${uuid}`, formData)
      .pipe(first());
  }

  public deleteContractInsuranceCompany(uuid: string): Observable<any> {
    return this.httpClient.delete(`/rest/crm/contract-insurance-company/${uuid}`)
      .pipe(first());
  }

  public downloadInsuranceCompaniesReport(filters: any): Observable<Uuid>  {
    return this.httpClient.post(`/rest/crm/contract-insurance-company/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }


  public downloadInsuranceCompaniesAdditionalReport(filters: any): Observable<Uuid>  {
    return this.httpClient.post(`/rest/crm/contract-insurance-company/additional/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  public getOfwcaReassigns(contractId: string): Observable<ContractOfwcaReassign[]> {
    return this.httpClient.get(`/rest/crm/contract/${contractId}/ofwca-reassigns`)
      .pipe(
        map((response: ListResponse<ContractOfwcaReassign[]>) => response.items),
        first()
      ) as Observable<ContractOfwcaReassign[]>
  }

  public updateOfwcaReassign(contractId: string, formData: any): Observable<any> {
    return this.httpClient.put(`/rest/crm/contract/${contractId}/update-ofwca-reassign`, formData)
      .pipe(first());
  }

  public getOfwcas(contractId: string): Observable<ListResponse<Ofwca>> {
    return this.httpClient.get(`/rest/crm/contract/${contractId}/ofwca`)
      .pipe(
        first()
      ) as Observable<ListResponse<Ofwca>>;
  }

  public updateAllContractAnnexes(contractId: string, field: string): Observable<any> {
    return this.httpClient.put(`/rest/crm/contract/${contractId}/update-annexes`, {field})
      .pipe(first());
  }

  public getOfwcaApplications(contractId: string): Observable<ListResponse<ContractOfwcaApplication>> {
    return this.httpClient.get(`/rest/crm/contract/${contractId}/ofwca-application`)
      .pipe(
        first()
      ) as Observable<ListResponse<ContractOfwcaApplication>>;
  }

  public updateOfwcaApplication(
    contractId: string,
    contractOfwcaApplicationId: string,
    formData: { status: number; note: string }
  ): Observable<any> {
    return this.httpClient.put(`/rest/crm/contract/${contractId}/ofwca-application/${contractOfwcaApplicationId}`, formData)
      .pipe(first());
  }
  public downloadCustomReport(filters: object): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/contract/custom/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }
  public getCustomReportSettings(reportName: string): Observable<ReportCustom> {
    return this.httpClient.get(`/rest/crm/report/custom/settings/${reportName}`)
      .pipe(first()) as Observable<ReportCustom>
      ;
  }
  public sendCustomReportSettings(formData: object): Observable<any> {
    return this.httpClient.post('/rest/crm/report/custom/settings', formData)
      .pipe(take(1));
  }
  public listOfVatInformation(nip: string): Observable<VatTaxpayers> {
    return this.httpClient.get(`/rest/crm/contract/list-of-vat/${nip}`)
      .pipe(first()) as Observable<VatTaxpayers> ;
  }
  public changeInvoiceNumber(formData: any, uuid: string): Observable<any> {
    return this.httpClient.post(`/rest/crm/contract/${uuid}/changeInvoiceNumber`, formData)
      .pipe(first());
  }
}
